import React, { useEffect, useState } from "react";
import { Link, useLocation } from "react-router-dom";

// Versioned storage key — bump the suffix to re-show the modal in the future
// (e.g. when a new event is announced). The flag is now only written when
// the user explicitly opts out via the "don't remind me" checkbox; without
// it, the modal reappears on every login.
const STORAGE_KEY_PREFIX = "postaway_summit_modal_v3_2027_dontremind_";
const ANONYMOUS_AUDIENCE_KEY = "anonymous";
// Slight delay so the modal doesn't feel jarring the instant the page loads.
const SHOW_DELAY_MS = 1200;

const readEmail = () => {
  if (typeof window === "undefined") return "";
  try {
    return window.localStorage.getItem("postaway_email") || "";
  } catch (_) {
    return "";
  }
};

const hasOptedOut = (audienceKey) => {
  if (!audienceKey || typeof window === "undefined") return false;
  try {
    return Boolean(window.localStorage.getItem(`${STORAGE_KEY_PREFIX}${audienceKey}`));
  } catch (_) {
    return false;
  }
};

const markOptedOut = (audienceKey) => {
  if (!audienceKey || typeof window === "undefined") return;
  try {
    window.localStorage.setItem(`${STORAGE_KEY_PREFIX}${audienceKey}`, "1");
  } catch (_) {}
};

// Checkout pages run their own exit-intent modal (ExitIntentModal); keep the
// summit promo off them so two popups never stack over the plan cards. The
// Inner Circle pages have their own embedded checkout.
const SUPPRESSED_PATHS = ["/success", "/host/checkout", "/inner-circle", "/inner-circle/confirmation"];

const getAudienceKey = (pathname) => {
  const email = readEmail();
  if (email) return email;
  if (pathname === "/") return ANONYMOUS_AUDIENCE_KEY;
  return null;
};

const SummitPromoModal = () => {
  const location = useLocation();
  const [open, setOpen] = useState(false);
  const [audienceKey, setAudienceKey] = useState("");
  const [dontRemind, setDontRemind] = useState(false);

  useEffect(() => {
    if (SUPPRESSED_PATHS.includes(location.pathname)) {
      setOpen(false);
      return undefined;
    }
    const nextAudienceKey = getAudienceKey(location.pathname);
    if (!nextAudienceKey) {
      setOpen(false);
      return undefined;
    }
    if (hasOptedOut(nextAudienceKey)) {
      setOpen(false);
      return undefined;
    }

    setAudienceKey(nextAudienceKey);
    const timer = setTimeout(() => setOpen(true), SHOW_DELAY_MS);
    return () => clearTimeout(timer);
  }, [location.pathname]);

  // Lock body scroll while open and close on Escape.
  useEffect(() => {
    if (!open || typeof document === "undefined") return undefined;
    const previousOverflow = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    const onKeyDown = (e) => {
      if (e.key === "Escape") handleDismiss();
    };
    document.addEventListener("keydown", onKeyDown);
    return () => {
      document.body.style.overflow = previousOverflow;
      document.removeEventListener("keydown", onKeyDown);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  const handleDismiss = () => {
    if (dontRemind) markOptedOut(audienceKey);
    setOpen(false);
  };

  const handleApply = () => {
    if (dontRemind) markOptedOut(audienceKey);
    setOpen(false);
  };

  if (!open) return null;

  return (
    <div
      className="summit-promo-modal-backdrop"
      role="dialog"
      aria-modal="true"
      aria-labelledby="summit-promo-modal-title"
      onClick={handleDismiss}
    >
      <div
        className="summit-promo-modal-card"
        onClick={(e) => e.stopPropagation()}
      >
        <button
          type="button"
          className="summit-promo-modal-close"
          aria-label="Close"
          onClick={handleDismiss}
        >
          <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round">
            <line x1="18" y1="6" x2="6" y2="18" />
            <line x1="6" y1="6" x2="18" y2="18" />
          </svg>
        </button>

        <div className="summit-promo-modal-scroll">
          <img
            className="summit-promo-modal-image"
            src="/summit-2027-cabin.jpg"
            alt="Guest wrapped in a blanket on the porch of a forest cabin"
          />

          <div className="summit-promo-modal-body">
          <div className="summit-promo-modal-eyebrow">
            Postaway Hospitality Summit 2027
          </div>
          <h2 id="summit-promo-modal-title" className="summit-promo-modal-title">
            See you in the Rockies next fall.
          </h2>
          <div className="summit-promo-modal-meta">
            September 24–26, 2027 · Breckenridge, Colorado
          </div>
          <p className="summit-promo-modal-text">
            Join us next fall for the Postaway Hospitality Summit in beautiful
            Breckenridge, Colorado!! Network with the best in the business
            while learning skills that will elevate your listing and/or lifestyle content. Enjoy
            informative sessions while being surrounded by golden Aspen leaves
            and mountain scenery. It doesn&rsquo;t get any better than fall in
            the Rockies. STR hosts, boutique hotel owners and hospitality
            creators are welcome.{" "}
            <Link
              to="/hospitality-summit-2027"
              className="summit-promo-modal-inline-link"
              onClick={handleApply}
            >
              Register now
            </Link>{" "}
            and we hope to see you in September 2027!
          </p>
          <div className="summit-promo-modal-actions">
            <Link
              to="/hospitality-summit-2027"
              className="summit-promo-modal-btn primary"
              onClick={handleApply}
            >
              Register Now
            </Link>
            <button
              type="button"
              className="summit-promo-modal-btn secondary"
              onClick={handleDismiss}
            >
              Maybe later
            </button>
          </div>
          <label className="summit-promo-modal-optout">
            <input
              type="checkbox"
              checked={dontRemind}
              onChange={(e) => setDontRemind(e.target.checked)}
            />
            <span>Please don&rsquo;t remind me of this again</span>
          </label>
          </div>
        </div>
      </div>
      <SummitPromoModalStyles />
    </div>
  );
};

const SummitPromoModalStyles = () => (
  <style>{`
    .summit-promo-modal-backdrop {
      position: fixed;
      inset: 0;
      z-index: 9999;
      background: rgba(15, 8, 75, 0.55);
      display: flex;
      align-items: center;
      justify-content: center;
      padding: 24px;
      box-sizing: border-box;
      animation: summitPromoFadeIn 180ms ease-out;
      /* Use dynamic viewport units so iOS Safari URL bar doesn't push the
         modal partially off-screen. Fallback to 100vh for older browsers. */
      height: 100vh;
      height: 100dvh;
      overflow-y: auto;
    }
    @keyframes summitPromoFadeIn {
      from { opacity: 0; }
      to { opacity: 1; }
    }
    .summit-promo-modal-card {
      position: relative;
      background: #ffffff;
      border-radius: 18px;
      max-width: 520px;
      width: 100%;
      max-height: calc(100vh - 48px);
      max-height: calc(100dvh - 48px);
      display: flex;
      flex-direction: column;
      overflow: hidden;
      box-shadow: 0 24px 64px rgba(15, 8, 75, 0.35);
      animation: summitPromoSlideIn 220ms cubic-bezier(0.2, 0.9, 0.3, 1);
      box-sizing: border-box;
    }
    .summit-promo-modal-scroll {
      overflow-y: auto;
      -webkit-overflow-scrolling: touch;
      overscroll-behavior: contain;
    }
    @keyframes summitPromoSlideIn {
      from { transform: translateY(20px); opacity: 0; }
      to { transform: translateY(0); opacity: 1; }
    }
    @media (max-width: 640px) {
      .summit-promo-modal-backdrop { padding: 12px; }
      .summit-promo-modal-card {
        max-height: calc(100vh - 24px);
        max-height: calc(100dvh - 24px);
        border-radius: 16px;
      }
    }
    .summit-promo-modal-close {
      position: absolute;
      top: 12px;
      right: 12px;
      width: 40px;
      height: 40px;
      border-radius: 50%;
      border: none;
      background: rgba(255, 255, 255, 0.95);
      color: #0f084b;
      cursor: pointer;
      display: inline-flex;
      align-items: center;
      justify-content: center;
      box-shadow: 0 2px 10px rgba(15, 8, 75, 0.25);
      transition: background 0.15s, transform 0.15s;
      z-index: 3;
    }
    .summit-promo-modal-close:hover {
      background: #ffffff;
      transform: scale(1.05);
    }
    .summit-promo-modal-image {
      display: block;
      width: 100%;
      aspect-ratio: 3 / 2;
      object-fit: cover;
      object-position: 50% 58%;
      background: #f4f3fb;
    }
    @media (max-width: 640px) {
      .summit-promo-modal-image { aspect-ratio: 4 / 3; object-position: 50% 62%; }
    }
    .summit-promo-modal-body {
      padding: 22px 26px 26px;
      display: flex;
      flex-direction: column;
      gap: 10px;
    }
    @media (max-width: 640px) {
      .summit-promo-modal-body { padding: 18px 18px 22px; gap: 8px; }
    }
    .summit-promo-modal-eyebrow {
      font-family: 'Neue Einstellung', 'Poppins', sans-serif;
      font-size: 11px;
      font-weight: 700;
      letter-spacing: 0.2em;
      text-transform: uppercase;
      color: #dc1d08;
    }
    .summit-promo-modal-title {
      font-family: 'Sailors', serif;
      font-size: 28px;
      color: #0f084b;
      margin: 0;
      line-height: 1.15;
    }
    @media (max-width: 640px) {
      .summit-promo-modal-title { font-size: 22px; }
    }
    .summit-promo-modal-meta {
      font-family: 'Neue Einstellung', 'Poppins', sans-serif;
      font-size: 12px;
      font-weight: 600;
      letter-spacing: 0.06em;
      text-transform: uppercase;
      color: #0f084b;
    }
    .summit-promo-modal-text {
      font-family: 'Neue Einstellung', 'Poppins', sans-serif;
      font-size: 14.5px;
      line-height: 1.55;
      color: #353945;
      margin: 6px 0 0;
    }
    @media (max-width: 640px) {
      .summit-promo-modal-text { font-size: 14px; }
    }
    .summit-promo-modal-inline-link {
      color: #dc1d08;
      font-weight: 700;
      text-decoration: underline;
      text-underline-offset: 2px;
    }
    .summit-promo-modal-inline-link:hover {
      color: #b51706;
    }
    .summit-promo-modal-actions {
      display: flex;
      gap: 10px;
      margin-top: 14px;
      flex-wrap: wrap;
    }
    @media (max-width: 480px) {
      .summit-promo-modal-actions { flex-direction: column-reverse; gap: 8px; }
    }
    .summit-promo-modal-btn {
      display: inline-flex;
      align-items: center;
      justify-content: center;
      padding: 11px 22px;
      border-radius: 100px;
      font-family: 'Neue Einstellung', 'Poppins', sans-serif;
      font-size: 14.5px;
      font-weight: 600;
      text-decoration: none;
      border: 2px solid transparent;
      cursor: pointer;
      transition: all 0.18s;
      flex: 1;
      min-height: 44px;
      text-align: center;
    }
    @media (max-width: 480px) {
      .summit-promo-modal-btn { width: 100%; flex: 0 0 auto; }
    }
    .summit-promo-modal-btn.primary {
      background: #dc1d08;
      color: #ffffff;
      border-color: #dc1d08;
    }
    .summit-promo-modal-btn.primary:hover {
      background: #b51706;
      border-color: #b51706;
    }
    .summit-promo-modal-btn.secondary {
      background: transparent;
      color: #0f084b;
      border-color: #0f084b;
    }
    .summit-promo-modal-btn.secondary:hover {
      background: #0f084b;
      color: #ffffff;
    }
    .summit-promo-modal-optout {
      display: flex;
      align-items: center;
      gap: 8px;
      margin-top: 14px;
      font-family: 'Neue Einstellung', 'Poppins', sans-serif;
      font-size: 13px;
      color: #6b7280;
      cursor: pointer;
      user-select: none;
    }
    .summit-promo-modal-optout input[type="checkbox"] {
      width: 16px;
      height: 16px;
      accent-color: #0f084b;
      cursor: pointer;
      margin: 0;
      flex-shrink: 0;
    }
    .summit-promo-modal-optout:hover { color: #0f084b; }
  `}</style>
);

export default SummitPromoModal;
